import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { pxToRem, withAlpha } from "../../styles/utils";

export const SidebarContainer = styled.aside`
  display: flex;
  flex-direction: column;
  min-width: max(${pxToRem(245)}, 196px);
  flex-shrink: 0;
  border-radius: ${pxToRem(16)};
  padding: ${pxToRem(8)};
  margin-right: ${pxToRem(12)};
  box-shadow: ${pxToRem(2)} ${pxToRem(6)} ${pxToRem(29)} 0
    ${({ theme }) => withAlpha(theme.colors.black, 0.09)};
  background: ${({ theme }) => theme.colors.white};
  min-height: 100%;
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${pxToRem(68)};
  padding: ${pxToRem(10)} ${pxToRem(8)};
  margin: 0;
`;

export const SidebarBrandImage = styled.img`
  display: block;
  width: min(100%, ${pxToRem(205)});
  height: auto;
  max-height: ${pxToRem(64)};
  object-fit: contain;
  object-position: left center;
`;

export const SidebarBrandText = styled.span`
  position: absolute;
  width: ${pxToRem(1)};
  height: ${pxToRem(1)};
  padding: 0;
  margin: ${pxToRem(-1)};
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const SidebarMenu = styled.nav`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin-top: ${pxToRem(10)};
  overflow-y: auto;
  scrollbar-width: none;
  scrollbar-color: ${({ theme }) => theme.colors.brandStrong}
    ${({ theme }) => theme.colors.brandTransparent};

  &::-webkit-scrollbar {
    width: ${pxToRem(8)};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.brandStrong};
    border-radius: ${pxToRem(4)};
  }
`;

export const SidebarGroup = styled.div`
  margin-bottom: ${pxToRem(10)};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SidebarTitle = styled.h3`
  font-size: ${pxToRem(14)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grayDisabled};
  padding-left: ${pxToRem(16)};
  margin: 0;
`;

export const SidebarItem = styled.li`
  list-style: none;
  margin: ${pxToRem(6)} 0;

  a {
    text-decoration: none;
  }
`;

export const SidebarLink = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: ${pxToRem(12)};
  border-radius: ${pxToRem(12)};
  color: ${({ theme }) => theme.colors.black};

  span {
    font-size: ${pxToRem(18)};
    font-weight: 400;
  }

  ${(props) =>
    props.$isActive &&
    css`
      background: ${({ theme }) => theme.colors.brand};
      color: ${({ theme }) => theme.colors.white};

      span {
        color: ${({ theme }) => theme.colors.white};
      }
    `}

  &:hover {
    background: ${({ theme }) => theme.colors.brandDark};
    color: ${({ theme }) => theme.colors.white};

    span {
      color: ${({ theme }) => theme.colors.white};
    }
  }
`;

export const SidebarIcon = styled.svg`
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};
  margin-right: ${pxToRem(12)};
  flex: 0 0 auto;
  color: inherit;
  opacity: 0.9;
`;

export const SidebarText = styled.span`
  font-size: ${pxToRem(18)};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ink};
`;

export const SidebarFooter = styled.div`
  position: relative;
  margin-top: auto;
  padding-top: ${pxToRem(8)};
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const SidebarUserTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${pxToRem(10)};
  padding: ${pxToRem(8)} ${pxToRem(10)};
  border: 0;
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: none;
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.ink, 0.04)};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const SidebarUserInfo = styled.div`
  min-width: 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${pxToRem(10)};
`;

export const SidebarUserIdentity = styled.div`
  min-width: 0;
  min-height: ${pxToRem(38)};
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const SidebarUserName = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SidebarUserEmail = styled.div`
  overflow: hidden;
  margin-top: ${pxToRem(2)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 400;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SidebarUserMenuIndicator = styled.svg<{ $open?: boolean }>`
  width: ${pxToRem(16)};
  height: ${pxToRem(16)};
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.inkMuted};
  transform: rotate(${({ $open }) => ($open ? "0deg" : "180deg")});
  transition: transform 0.15s ease;
`;

export const SidebarUserMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + ${pxToRem(6)});
  z-index: 30;
  padding: ${pxToRem(6)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const sidebarUserMenuItemStyles = css`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${pxToRem(9)};
  padding: ${pxToRem(8)} ${pxToRem(10)};
  border: 0;
  border-radius: ${pxToRem(8)};
  background: none;
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 400;
  line-height: 1.35;
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    flex: 0 0 auto;
    color: ${({ theme }) => theme.colors.inkMuted};
  }

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.ink, 0.04)};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(-2)};
  }
`;

export const SidebarUserMenuLink = styled(Link)`
  ${sidebarUserMenuItemStyles}
`;

export const SidebarUserMenuItem = styled.button<{ $danger?: boolean }>`
  ${sidebarUserMenuItemStyles}

  ${({ $danger, theme }) =>
    $danger &&
    css`
      color: ${theme.colors.danger};

      svg {
        color: currentColor;
      }

      &:hover {
        background: ${withAlpha(theme.colors.danger, 0.08)};
      }
    `}
`;

export const SidebarUserMenuDivider = styled.div`
  height: ${pxToRem(1)};
  margin: ${pxToRem(6)} ${pxToRem(4)};
  background: ${({ theme }) => theme.borders.divider};
`;

export const SidebarUserSkeleton = styled.div<{ $width: number; $height: number }>`
  width: ${({ $width }) => pxToRem($width)};
  max-width: 100%;
  height: ${({ $height }) => pxToRem($height)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  background: ${({ theme }) => withAlpha(theme.colors.ink, 0.07)};

  & + & {
    margin-top: ${pxToRem(6)};
  }
`;
